import { Component } from 'preact';
import { motion } from 'framer-motion';
import { UI } from '../utils/arena/uiSounds';
import { BOOSTER_SETS } from '../utils/arena/packsApi';
import { DRAFT_VISIBILITY, entryCostForMode, keystoneCostForMode } from '../utils/arena/draftApi';
import { SET_UNLOCK_LEVELS, levelFromXp } from '../utils/arena/profileDefaults';
import { loadModeSetup, saveModeSetup } from '../utils/arena/gameModes';
import {
  GOLD, TEXT_PRIMARY, TEXT_BODY, TEXT_MUTED, ACCENT_GOLD, COIN_COLOR,
  BEVELED_BTN, GOLD_BTN,
  FourCorners, OrnamentalDivider, VIGNETTE,
  TEXTURED_PANEL_STYLE, SUB_PANEL_STYLE,
} from '../lib/medievalTheme';
import { t, k } from '../localization/t';
import { isFreePlay } from '../utils/freePlay';
import { Select } from './ui/select';
import { isPhoneLayout } from '../utils/platform';
import { Tooltip } from './ui/tooltip';
import { CoinIcon, KeystoneIcon, IconWarning } from './ui/icons';

// 1cqw = 1rem at design scale, so the same numbers render the identical
// look: canvas-relative on desktop, device-absolute (tappable) on phone.
const U = isPhoneLayout() ? 'rem' : 'cqw';

// Scheduled-start pickers were removed per the unified instance
// flow. Drafts just start as soon as the pod fills (or the
// creator hits "Start Now" from the lobby). Server still requires
// a scheduledAt field, so we stamp one at submission time
// (5 minutes from now) to satisfy its validation without exposing
// the knob to the user.
function defaultScheduledAt() {
  return new Date(Date.now() + 5 * 60 * 1000).toISOString();
}

export function getUnlockedSetOptions(profile) {
  const level = profile ? levelFromXp(profile.xp || 0) : 1;
  return Object.entries(BOOSTER_SETS)
    .filter(([key]) => level >= (SET_UNLOCK_LEVELS[key] || 1))
    .map(([key, set]) => ({ value: key, label: set.label }));
}

// Pod sizes the create form actually lets you pick; 6 and 8 render as
// disabled tiles. Also gates which stored pod size is restorable.
const ENABLED_POD_SIZES = [2, 4];

const VISIBILITY_OPTIONS_BASE = [
  { value: DRAFT_VISIBILITY.PUBLIC, label: k('Public. Anyone can join') },
  { value: DRAFT_VISIBILITY.PRIVATE, label: k('Private. Invite friends') },
];

const HEADER_TITLE_STYLE = {
  fontSize: `1.625${U}`,
  lineHeight: 1.05,
  letterSpacing: '0.04em',
  fontWeight: 800,
  background: 'linear-gradient(180deg, #f7e4b6 0%, #d4a843 60%, #a8772d 100%)',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  backgroundClip: 'text',
  textShadow: '0 1px 0 rgba(0,0,0,0.4), 0 0 18px rgba(212,168,67,0.18)',
  fontFamily: "'FantaisieArtistique', cursive",
};

const SECTION_LABEL_STYLE = {
  color: `${GOLD} 0.55)`,
 fontSize: `0.625${U}`,
 fontWeight: 700,
 fontFamily: "'FantaisieArtistique', cursive",
};

// Segmented-button tile shells. Same active-state vocabulary as the
// format/HP tiles in ModeSetupDialog so pod-size and payment-method
// pickers read as siblings of those affordances. No inline cursor
// here; the global `button { cursor: url(...) }` rule supplies the
// medieval pointer.
const SEG_BASE = {
  ...SUB_PANEL_STYLE,
  borderRadius: `0.375${U}`,
  transition: 'transform 140ms ease, box-shadow 140ms ease, border-color 140ms ease',
  position: 'relative',
};

const SEG_ACTIVE = {
  ...SEG_BASE,
  border: `1px solid ${ACCENT_GOLD}`,
  boxShadow: [
    'inset 0 2px 4px rgba(0,0,0,0.5)',
    'inset 0 -1px 0 rgba(255,235,195,0.06)',
    `inset 0 0 0 1px ${GOLD} 0.2)`,
    'inset 0 0 22px rgba(0,0,0,0.4)',
    `0 0 1${U} ${GOLD} 0.25)`,
    `0 0 0.2${U} ${ACCENT_GOLD}`,
  ].join(', '),
};

export default class CreateDraftModal extends Component {
  constructor(props) {
    super(props);
    // Pre-select visibility when the caller already picked it
    // upstream (SessionBrowser's visibility tile → ModePickerScreen).
    // The `lockVisibility` prop hides the selector in that case so
    // the user isn't prompted twice for the same choice.
    const initialVisibility = props.initialVisibility || DRAFT_VISIBILITY.PUBLIC;
    // Default to spending Keystones when the player has enough. The
    // currency exists to grant free entries, so auto-preferring it is
    // the expected UX. Falls back to coins when the balance is short.
    // Sealed costs 2 keystones, draft costs 1.
    const ksCost = keystoneCostForMode(props.modeKey);
    const hasKeystone = (props.profile?.keystones || 0) >= ksCost;
    // Last confirmed setup for this mode. A stored set can have been
    // unlocked on another account (or the unlock table can shift), so
    // it only survives if it is still in this profile's unlocked list;
    // pod size only survives if it is one of the enabled sizes below.
    const saved = loadModeSetup(props.modeKey);
    const unlocked = getUnlockedSetOptions(props.profile);
    const setUnlocked = unlocked.some((o) => o.value === saved.setKey);
    // A looking-for-match pairing arrives with the set both players agreed on.
    const preset = unlocked.some((o) => o.value === props.presetSetKey) ? props.presetSetKey : null;
    this.state = {
      setKey: preset || (setUnlocked ? saved.setKey : 'beta'),
      podSize: ENABLED_POD_SIZES.includes(saved.podSize) ? saved.podSize : 2,
      // Session-wide automation mode for every round of the event. Off by
      // default, matching ModeSetupDialog. The automation engine covers only
      // part of the card pool, so the predictable table resolves nothing.
      automations: saved.automations === true,
      visibility: initialVisibility,
      // A hub challenge arrives with its target already invited.
      invitedFriends: Array.isArray(props.presetInvitees) ? props.presetInvitees : [],
      friendSearch: '',
      paymentMethod: hasKeystone ? 'keystone' : 'coins',
      submitting: false,
      error: null,
    };
  }

  getPodSize() {
    return this.state.podSize;
  }

  handleSubmit = async () => {
    const { onCreate, modeKey } = this.props;
    const { setKey, visibility, invitedFriends, paymentMethod, automations } = this.state;
    const podSize = this.getPodSize();
    const isSealed = modeKey === 'sealed';

    if (visibility === DRAFT_VISIBILITY.PRIVATE && invitedFriends.length === 0) {
      this.setState({ error: isSealed ? t('Invite at least one friend before creating a private sealed event.') : t('Invite at least one friend before creating a private draft.') });
      return;
    }

    this.setState({ submitting: true, error: null });
    saveModeSetup(modeKey, { setKey, podSize, automations });
    try {
      await onCreate({
        setKey,
        podSize,
        // Always 5 minutes from submission. The server validates
        // scheduledAt > now and uses the timestamp to arm its
        // auto-start timer. Creators can start early via the
        // DraftLobby "Start Now" button regardless.
        scheduledAt: defaultScheduledAt(),
        visibility,
        invitePlayerIds: visibility === DRAFT_VISIBILITY.PRIVATE ? invitedFriends.map((f) => f.id) : undefined,
        paymentMethod,
        automations,
      });
    } catch (err) {
      this.setState({ submitting: false, error: err.message });
    }
  };

  toggleFriendInvite = (friend) => {
    this.setState((s) => {
      const exists = s.invitedFriends.some((f) => f.id === friend.id);
      return {
        invitedFriends: exists
          ? s.invitedFriends.filter((f) => f.id !== friend.id)
          : [...s.invitedFriends, friend],
      };
    });
  };

  getFilteredFriends() {
    const { friendListData } = this.props;
    const friends = friendListData?.friends || [];
    const q = this.state.friendSearch.toLowerCase().trim();
    if (!q) return friends;
    return friends.filter((f) => f.name.toLowerCase().includes(q));
  }

  renderSectionLabel(text) {
    return (
      <div className="flex items-center gap-2 mb-2">
        <div className="h-px w-3" style={{ background: `linear-gradient(90deg, transparent 0%, ${GOLD} 0.55) 100%)` }} />
        <span style={SECTION_LABEL_STYLE}>{text}</span>
        <div className="h-px flex-1" style={{ background: `linear-gradient(90deg, ${GOLD} 0.55) 0%, transparent 100%)` }} />
      </div>
    );
  }

  render() {
    const { onClose, profile, lockVisibility, title, modeKey } = this.props;
    const { setKey, podSize, visibility, invitedFriends, submitting, error, automations } = this.state;

    const isSealed = modeKey === 'sealed';
    const entryCost = entryCostForMode(modeKey);
    const ksCost = keystoneCostForMode(modeKey);
    const packsCount = isSealed ? 6 : 3;
    const modeLabel = isSealed ? t('Sealed') : t('Draft');

    const showFriendPicker = visibility === DRAFT_VISIBILITY.PRIVATE;
    const playerCoins = profile?.coins || 0;
    const playerKeystones = profile?.keystones || 0;
    const { paymentMethod } = this.state;
    const usingKeystone = paymentMethod === 'keystone' && playerKeystones >= ksCost;
    const canAffordGold = playerCoins >= entryCost;
    // Free play charges no entry fee: no cost panel, nothing to afford.
    const freePlay = isFreePlay();
    const canAfford = freePlay || (usingKeystone ? playerKeystones >= ksCost : canAffordGold);
    const needsInvite = showFriendPicker && invitedFriends.length === 0;
    const setOptions = getUnlockedSetOptions(profile);

    const visibilityOptions = VISIBILITY_OPTIONS_BASE.map((o) => ({ ...o, label: t(o.label) }));

    return (
      <div
        className="fixed inset-0 z-[200] flex items-center justify-center"
        style={{ background: 'rgba(0,0,0,0.78)', backdropFilter: 'blur(2px)' }}
      >
        <div className="fixed inset-0 pointer-events-none" style={{ background: VIGNETTE }} />

        <motion.div
          initial={{ opacity: 0, y: 12, scale: 0.985 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 8, scale: 0.99 }}
          transition={{ duration: 0.28, ease: [0.22, 0.8, 0.36, 1] }}
          className={isPhoneLayout()
            ? 'relative w-full h-full flex flex-col overflow-hidden'
            : 'relative w-[38cqw] flex flex-col overflow-hidden'}
          style={isPhoneLayout()
            // Phone: full-screen view, not a floating modal. Solid
            // backgroundColor: no dim overlay behind a full-screen view,
            // so the 0.92-alpha panel color would let the screen behind
            // bleed through.
            // border/ring shadows off: a full-screen view has no frame
            // (a rectangular stroke fights the rounded screen corners).
            ? { ...TEXTURED_PANEL_STYLE, borderRadius: 0, backgroundColor: '#0c0a08', border: 'none', boxShadow: 'inset 0 0 60px rgba(0,0,0,0.55)' }
            : { ...TEXTURED_PANEL_STYLE, maxHeight: '90cqh' }}
        >
          {isPhoneLayout() ? null : <FourCorners knots knotSize={14} knotOpacity={0.45} />}

          {/* Header. Phone: one-line compact bar (the stacked title +
              divider ate a third of a 390px-tall screen). */}
          {isPhoneLayout() ? (
            <div
              className="shrink-0 flex items-baseline gap-2 pb-1.5"
              style={{
                paddingLeft: 'max(1rem, env(safe-area-inset-left, 0px))',
                paddingRight: 'max(1rem, env(safe-area-inset-right, 0px))',
                paddingTop: 'max(0.625rem, env(safe-area-inset-top, 0px))',
              }}
            >
              <h2 style={{ ...HEADER_TITLE_STYLE, fontSize: '1.125rem' }}>{title || t('Create {mode} Event', { mode: modeLabel })}</h2>
            </div>
          ) : (
            <>
              <div className="px-7 pt-5 pb-3 text-center shrink-0">
                <h2 style={HEADER_TITLE_STYLE}>{title || t('Create {mode} Event', { mode: modeLabel })}</h2>
                <div
                  className="text-[0.6875cqw] mt-1"
                  style={{ color: `${GOLD} 0.5)`, fontFamily: "'FantaisieArtistique', cursive" }}
                >
                  {t('{mode} · Tournament Setup', { mode: modeLabel })}
                </div>
              </div>
              <div className="px-7 shrink-0">
                <OrnamentalDivider />
              </div>
            </>
          )}

          {/* Body */}
          <div
            className={isPhoneLayout()
              ? 'flex-1 min-h-0 overflow-y-auto medieval-scrollbar py-2 grid grid-cols-2 gap-x-5 gap-y-2 content-center w-full mx-auto'
              : 'flex-1 min-h-0 overflow-y-auto medieval-scrollbar px-7 py-5 flex flex-col gap-5'}
            style={isPhoneLayout() ? {
              maxWidth: '46rem',
              paddingLeft: 'max(1rem, env(safe-area-inset-left, 0px))',
              paddingRight: 'max(1rem, env(safe-area-inset-right, 0px))',
            } : undefined}>
            {/* Booster Set */}
            <div>
              {this.renderSectionLabel(t('Booster Set'))}
              <Select
                portalMenu
                ariaLabel={t('Booster Set')}
                options={setOptions}
                value={setKey}
                onValueChange={(v) => this.setState({ setKey: v })}
                placeholder={t('Select a set...')}
              />
            </div>

            {/* Pod Size. Segmented tile bar with the same active-state
                language as the ModeSetupDialog format/HP tiles. */}
            <div>
              {this.renderSectionLabel(t('Pod Size'))}
              <div className="grid grid-cols-4 gap-2">
                {[2, 4, 6, 8].map((n) => {
                  const enabled = ENABLED_POD_SIZES.includes(n);
                  const experimental = n === 4;
                  const isActive = podSize === n;
                  const baseStyle = isActive ? SEG_ACTIVE : SEG_BASE;
                  // Always render a button as the grid child so every
                  // cell sizes to its column equally. The original code
                  // wrapped disabled buttons in `<span className="flex">`
                  // for the Tooltip's hover capture, which shrank those
                  // cells to content width and broke the grid layout.
                  // `Tooltip` uses cloneElement so no wrapper is needed
                  // for enabled buttons; for the disabled case we still
                  // need a wrapper (a `disabled` button can't fire
                  // mouseenter), and `w-full h-full` ensures the span
                  // fills its grid cell so the tooltip target stays
                  // the same shape as the visible button.
                  const button = (
                    <button
                      type="button"
                      disabled={!enabled}
                      aria-disabled={!enabled}
                      className="w-full py-2.5 inline-flex items-center justify-center gap-1.5"
                      style={{
                        ...baseStyle,
                        opacity: enabled ? 1 : 0.4,
                      }}
                      data-sound={enabled ? UI.SELECT : undefined}
                      onClick={enabled ? () => this.setState({ podSize: n }) : undefined}
                    >
                      <span
                        className="tabular-nums font-bold"
                        style={{
                          fontSize: `1.125${U}`,
                          color: isActive ? ACCENT_GOLD : TEXT_BODY,
                          fontFamily: "'FantaisieArtistique', cursive",
                        }}
                      >
                        {n}
                      </span>
                      <span
                        className="text-[0.5625cqw]"
                        style={{ color: isActive ? `${GOLD} 0.85)` : `${GOLD} 0.4)` }}
                      >
                        {t('players')}
                      </span>
                      {experimental ? (
                        <IconWarning size={11} style={{ color: '#e6b34a', position: 'absolute', top: `0.3${U}`, right: `0.3${U}` }} />
                      ) : null}
                    </button>
                  );
                  if (!enabled) {
                    return (
                      <Tooltip key={n} content={t('Not yet available')} side="top">
                        <span className="flex w-full h-full">{button}</span>
                      </Tooltip>
                    );
                  }
                  if (experimental) {
                    return (
                      <Tooltip key={n} content={t('Experimental. Multi-round drafts may have bugs. Proceed with caution.')} side="top">
                        {button}
                      </Tooltip>
                    );
                  }
                  return <div key={n}>{button}</div>;
                })}
              </div>
            </div>

            {/* Automation. The session-wide play mode, applied to every
                round of the event. Same segmented vocabulary as Pod Size. */}
            <div>
              {this.renderSectionLabel(t('Automation'))}
              <div className="grid grid-cols-2 gap-2">
                {[
                  { value: false, label: t('Manual'), hint: t('Tabletop rules') },
                  { value: true, label: t('Automated'), hint: t('Experimental') },
                ].map((opt) => {
                  const isActive = automations === opt.value;
                  return (
                    <button
                      key={String(opt.value)}
                      type="button"
                      className="w-full py-2.5 inline-flex flex-col items-center justify-center gap-0.5"
                      style={isActive ? SEG_ACTIVE : SEG_BASE}
                      data-sound={UI.SELECT}
                      onClick={() => this.setState({ automations: opt.value })}
                    >
                      <span
                        className="font-bold"
                        style={{
                          fontSize: `0.875${U}`,
                          color: isActive ? ACCENT_GOLD : TEXT_BODY,
                          fontFamily: "'FantaisieArtistique', cursive",
                        }}
                      >
                        {opt.label}
                      </span>
                      <span
                        className="text-[0.5625cqw]"
                        style={{ color: isActive ? `${GOLD} 0.85)` : `${GOLD} 0.4)` }}
                      >
                        {opt.hint}
                      </span>
                    </button>
                  );
                })}
              </div>
              {isPhoneLayout() ? null : (
                <div className="text-[0.625cqw] mt-2 leading-snug" style={{ color: TEXT_MUTED }}>
                  {automations
                    ? t('The game resolves card effects, combat and turn steps for you. Support is still being added card by card, so expect gaps.')
                    : t('Nothing resolves on its own. Players move cards, tap, pay and settle combat themselves, exactly like playing across a real table.')}
                </div>
              )}
            </div>

            {/* Visibility */}
            {!lockVisibility ? (
              <div>
                {this.renderSectionLabel(t('Visibility'))}
                <Select
                  portalMenu
                  ariaLabel={t('Visibility')}
                  options={visibilityOptions}
                  value={visibility}
                  onValueChange={(v) => this.setState({ visibility: v })}
                />
              </div>
            ) : null}

            {/* Friend invite picker */}
            {showFriendPicker ? (
              <div>
                {this.renderSectionLabel(t('Invite Friends · {count}/{max}', { count: invitedFriends.length, max: podSize - 1 }))}
                {invitedFriends.length >= podSize - 1 ? (
                  <div
                    className="text-xs px-3 py-2 italic"
                    style={{ ...SUB_PANEL_STYLE, color: TEXT_MUTED }}
                  >
                    {t('Maximum players reached')}
                  </div>
                ) : (
                  <Select
                    portalMenu
                    ariaLabel={t('Invite friend')}
                    options={this.getFilteredFriends()
                      .filter((f) => !invitedFriends.some((inv) => inv.id === f.id))
                      .map((f) => ({ value: f.id, label: f.name || t('Unknown') }))}
                    value=""
                    onValueChange={(friendId) => {
                      const friend = (this.props.friendListData?.friends || []).find((f) => f.id === friendId);
                      if (friend) this.toggleFriendInvite(friend);
                    }}
                    placeholder={t('Choose a friend to invite...')}
                    searchable
                    menuSearchPlaceholder={t('Type to filter friends...')}
                    noOptionsMessage={t('No friends available')}
                  />
                )}
                {needsInvite ? (
                  <div
                    className="text-[0.6875cqw] italic mt-2"
                    style={{ color: '#e0a060' }}
                    aria-live="polite"
                  >
                    {isSealed ? t('Private sealed events must include at least one invited friend.') : t('Private drafts must include at least one invited friend.')}
                  </div>
                ) : null}
                {invitedFriends.length > 0 ? (
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {invitedFriends.map((friend) => (
                      <button
                        key={friend.id}
                        type="button"
                        className="inline-flex items-center gap-1 px-2 py-1 text-[0.6875cqw] font-semibold transition-all"
                        style={{
                          background: `${GOLD} 0.14)`,
                          border: `1px solid ${GOLD} 0.35)`,
                          borderRadius: `0.25${U}`,
                          color: ACCENT_GOLD,
                          fontFamily: "'FantaisieArtistique', cursive",
                        }}
                        onClick={() => this.toggleFriendInvite(friend)}
                        title={t('Click to remove')}
                      >
                        {friend.name}
                        <span style={{ color: TEXT_MUTED, fontSize: `0.625${U}`, marginLeft: `0.25${U}` }}>✕</span>
                      </button>
                    ))}
                  </div>
                ) : null}
              </div>
            ) : null}

            {/* Entry cost panel. Recessed sub-panel containing the
                pay-with toggle (when a Keystone is available) and a
                live readout of the cost + the player's balance. */}
            {freePlay ? null : (
            <div>
              {this.renderSectionLabel(t('Entry Cost'))}
              <div className="px-3 py-3 flex flex-col gap-2" style={SUB_PANEL_STYLE}>
                {playerKeystones >= ksCost ? (
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      className="py-2 inline-flex items-center justify-center gap-1.5"
                      style={usingKeystone ? SEG_ACTIVE : SEG_BASE}
                      data-sound={UI.SELECT}
                      onClick={() => this.setState({ paymentMethod: 'keystone' })}
                      title={ksCost === 1 ? t('Spend 1 Keystone for a free entry') : t('Spend {n} Keystones for a free entry', { n: ksCost })}
                    >
                      <KeystoneIcon size={12} />
                      <span
                        className="text-[0.6875cqw] font-semibold"
                        style={{ color: usingKeystone ? ACCENT_GOLD : TEXT_BODY, fontFamily: "'FantaisieArtistique', cursive" }}
                      >
                        {ksCost === 1 ? t('1 Keystone') : t('{n} Keystones', { n: ksCost })}
                      </span>
                    </button>
                    <button
                      type="button"
                      className="py-2 inline-flex items-center justify-center gap-1.5"
                      style={!usingKeystone ? SEG_ACTIVE : SEG_BASE}
                      data-sound={UI.SELECT}
                      onClick={() => this.setState({ paymentMethod: 'coins' })}
                      title={t('Pay {n} gold instead', { n: entryCost })}
                    >
                      <span
                        className="text-[0.6875cqw] font-semibold"
                        style={{ color: !usingKeystone ? ACCENT_GOLD : TEXT_BODY, fontFamily: "'FantaisieArtistique', cursive" }}
                      >
                        {entryCost}
                      </span>
                      <CoinIcon size={12} />
                    </button>
                  </div>
                ) : null}

                {usingKeystone ? (
                  <>
                    <div className="flex items-center justify-between">
                      <span className="text-[0.6875cqw]" style={{ color: TEXT_MUTED }}>{t('Entry cost')}</span>
                      <span className="flex items-center gap-1 text-sm font-bold tabular-nums" style={{ color: '#c9a86a', fontFamily: "'FantaisieArtistique', cursive" }}>
                        {ksCost}<KeystoneIcon size={13} />
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-[0.6875cqw]" style={{ color: TEXT_MUTED }}>{t('Your Keystones')}</span>
                      <span className="flex items-center gap-0.5 text-[0.75cqw] font-semibold tabular-nums" style={{ color: TEXT_BODY }}>
                        {playerKeystones}<KeystoneIcon size={10} />
                      </span>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="flex items-center justify-between">
                      <span className="text-[0.6875cqw]" style={{ color: TEXT_MUTED }}>{t('Entry cost ({n} packs, 20% off)', { n: packsCount })}</span>
                      <span className="flex items-center gap-1 text-sm font-bold tabular-nums" style={{ color: COIN_COLOR, fontFamily: "'FantaisieArtistique', cursive" }}>
                        {entryCost}<CoinIcon size={13} />
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-[0.6875cqw]" style={{ color: TEXT_MUTED }}>{t('Your balance')}</span>
                      <span className="flex items-center gap-0.5 text-[0.75cqw] font-semibold tabular-nums" style={{ color: canAffordGold ? TEXT_BODY : '#e06060' }}>
                        {playerCoins}<CoinIcon size={10} />
                      </span>
                    </div>
                  </>
                )}

                {/* Feedback slot. Fixed-height so the panel doesn't
                    grow/shrink when the affordance state flips. */}
                <div
                  className="text-[0.6875cqw] italic"
                  style={{ color: '#e06060', minHeight: `1.1${U}` }}
                  aria-live="polite"
                >
                  {!usingKeystone && !canAffordGold
                    ? t('Not enough coins. You need {n} more', { n: entryCost - playerCoins })
                    : ' '}
                </div>
              </div>
            </div>
            )}

            {/* Submission error slot */}
            <div
              className="text-sm px-3 py-2"
              style={{
                color: '#e06060',
                background: error ? 'rgba(224,96,96,0.12)' : 'transparent',
                border: error ? '1px solid rgba(224,96,96,0.32)' : '1px solid transparent',
                borderRadius: `0.375${U}`,
                minHeight: `2${U}`,
              }}
              aria-live="polite"
            >
              {error || ' '}
            </div>
          </div>

          {isPhoneLayout() ? null : (
            <div className="px-7 shrink-0">
              <OrnamentalDivider />
            </div>
          )}

          {/* Footer */}
          <div
            className={isPhoneLayout() ? 'flex justify-end gap-2 pt-2 shrink-0' : 'flex justify-end gap-3 px-7 py-4 shrink-0'}
            style={{
              background: 'linear-gradient(180deg, rgba(0,0,0,0.18) 0%, rgba(0,0,0,0.42) 100%)',
              borderTop: `1px solid ${GOLD} 0.08)`,
              ...(isPhoneLayout() ? {
                paddingLeft: 'max(1rem, env(safe-area-inset-left, 0px))',
                paddingRight: 'max(1rem, env(safe-area-inset-right, 0px))',
                paddingBottom: 'max(0.5rem, env(safe-area-inset-bottom, 0px))',
              } : {}),
            }}
          >
            <button
              type="button"
              className="px-5 py-2 text-sm transition-all duration-150 hover:scale-[1.02] active:scale-[0.98]"
              style={{
                ...BEVELED_BTN,
                borderRadius: `0.375${U}`,
                fontFamily: "'FantaisieArtistique', cursive",
                letterSpacing: '0.04em',
                color: TEXT_BODY,
              }}
              data-sound={UI.CANCEL}
              onClick={onClose}
            >
              {t('Cancel')}
            </button>
            <button
              type="button"
              className="px-6 py-2 text-sm font-semibold transition-all duration-150 hover:scale-[1.03] active:scale-[0.98] disabled:opacity-45 disabled:hover:scale-100"
              style={{
                ...(canAfford ? GOLD_BTN : BEVELED_BTN),
                borderRadius: `0.375${U}`,
                fontFamily: "'FantaisieArtistique', cursive",
                letterSpacing: '0.06em',
              }}
              data-sound={UI.CONFIRM}
              disabled={submitting || !canAfford || needsInvite}
              onClick={this.handleSubmit}
            >
              {submitting ? t('Creating...') : t('Create Event')}
            </button>
          </div>
        </motion.div>
      </div>
    );
  }
}
